import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, Mail, Link as LinkIcon, Copy, ShieldCheck, UserMinus, ChevronRight, LogOut, Trash2, Camera, CheckCircle2, MapPin, Navigation, Ban, CalendarDays, Share2 } from "lucide-react";
import { toast } from "sonner";
import { GoogleIcon, Spinner } from "@/components/peeps/ui";
import { api, mediaUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { colors, groupThemes, initials, themeOf, gradientCss } from "@/lib/theme";
import { pickAndUpload } from "@/lib/upload";
import { Avatar, Field, Input, PrimaryButton, Sheet, Toggle } from "@/components/peeps/ui";
import { PresenceDot } from "@/components/peeps/MemberStatusList";
import { CalendarSharingSection } from "@/components/peeps/CalendarSharingSection";

export default function GroupSettings() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [leaving, setLeaving] = useState(false);

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteUrl, setInviteUrl] = useState("");
  const [inviting, setInviting] = useState(false);

  const [name, setName] = useState("");
  const [theme, setTheme] = useState("violet");
  const [cover, setCover] = useState(null);
  const [savingGroup, setSavingGroup] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);

  // proximity + calendar + blocking
  const [groupSharing, setGroupSharing] = useState(true);
  const [calPref, setCalPref] = useState(null);
  const [calendars, setCalendars] = useState(null);
  const [loadingCals, setLoadingCals] = useState(false);
  const [blocked, setBlocked] = useState(new Set());
  const [statusMap, setStatusMap] = useState({});
  const [importFeed, setImportFeed] = useState(null);
  const isAppAdmin = !!user?.is_app_admin;

  const loadFeed = useCallback(async () => {
    if (!user?.is_app_admin) return;
    try { setImportFeed(await api.getGroupImportFeed(String(id))); } catch {}
  }, [id, user]);
  useEffect(() => { loadFeed(); }, [loadFeed]);

  const changeFeed = async (sg) => {
    setImportFeed((f) => ({ ...(f || {}), source_group: sg || null }));
    try {
      const res = await api.setGroupImportFeed(String(id), sg || null);
      if (sg) toast(`Feed set — ${res.created} imported, ${res.updated} updated`);
      else toast("Chat import disabled for this group");
    } catch (e) { toast(e.message || "Couldn't update feed"); loadFeed(); }
  };

  const loadExtras = useCallback(async () => {
    try {
      const [prox, cp, blocks, ms] = await Promise.all([
        api.getProximityPrefs().catch(() => null),
        api.getCalendarPrefs(String(id)).catch(() => null),
        api.myBlocks().catch(() => []),
        api.memberStatus(String(id)).catch(() => null),
      ]);
      if (prox) {
        const row = (prox.groups || []).find((g) => g.group_id === String(id));
        setGroupSharing(row ? row.sharing : (prox.scope === "global" && prox.default_enabled));
      }
      if (cp) setCalPref(cp);
      setBlocked(new Set((blocks || []).map((b) => b.id)));
      if (ms) setStatusMap(Object.fromEntries((ms.members || []).map((x) => [x.user_id, x.status])));
    } catch {}
  }, [id]);

  const load = useCallback(async () => {
    try {
      setData(await api.getGroup(String(id)));
    } catch (e) {
      toast(e.message || "Couldn't load");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadExtras(); }, [loadExtras]);

  // Handle return from the Google Calendar consent redirect.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("cal") === "connected") {
      toast("Google Calendar connected — new plans will sync");
      load();
      window.history.replaceState(null, "", window.location.pathname);
    } else if (params.get("cal_error")) {
      const map = { nogrant: "Google didn't grant Calendar access. Please try again.", sync: "Connected, but couldn't sync events yet.", token: "Google sign-in failed. Please try again.", state: "Session expired. Please try again.", consent: "Calendar access was cancelled." };
      toast(map[params.get("cal_error")] || "Couldn't connect Google Calendar.");
      window.history.replaceState(null, "", window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleGroupSharing = async (v) => {
    setGroupSharing(v);
    try {
      await api.setGroupProximity(String(id), v);
    } catch (e) {
      toast(e.message || "Couldn't update");
      loadExtras();
    }
  };

  const saveCalPref = async (patch) => {
    const next = { ...calPref, ...patch };
    setCalPref(next);
    try {
      const saved = await api.setCalendarPrefs(String(id), {
        destination: next.destination,
        personal_calendar_id: next.personal_calendar_id,
      });
      setCalPref(saved);
    } catch (e) {
      toast(e.message || "Couldn't save calendar preference");
      loadExtras();
    }
  };

  const pickPersonalDestination = async () => {
    setLoadingCals(true);
    try {
      const { calendars: list } = await api.listGoogleCalendars();
      setCalendars(list);
      if (list.length) {
        const primary = list.find((c) => c.primary) || list[0];
        await saveCalPref({ destination: "personal_calendar", personal_calendar_id: primary.id });
      }
    } catch (e) {
      toast(e.message || "Couldn't load your Google calendars");
    } finally {
      setLoadingCals(false);
    }
  };

  const toggleBlock = async (uid, name) => {
    const isBlocked = blocked.has(uid);
    const next = new Set(blocked);
    isBlocked ? next.delete(uid) : next.add(uid);
    setBlocked(next);
    try {
      isBlocked ? await api.unblockUser(uid) : await api.blockUser(uid);
      toast(isBlocked ? `Unblocked ${name}` : `Blocked ${name}`);
    } catch (e) {
      toast(e.message || "Couldn't update");
      loadExtras();
    }
  };

  const group = data?.group;
  const members = data?.members || [];
  const isAdmin = group?.my_role === "admin";
  const th = themeOf(group?.theme);

  const openEdit = () => {
    setName(group.name);
    setTheme(group.theme || "violet");
    setCover(group.cover_image_url || null);
    setShowEdit(true);
  };

  const chooseCover = async () => {
    setUploadingCover(true);
    try {
      const res = await pickAndUpload();
      if (res && res.error) toast(res.error);
      else if (res && res[0]) setCover(res[0]);
    } catch { toast("Couldn't upload"); }
    finally { setUploadingCover(false); }
  };

  const saveGroup = async () => {
    setSavingGroup(true);
    try {
      await api.updateGroup(String(id), { name: name.trim(), theme, cover_image_url: cover });
      setShowEdit(false);
      toast("Group updated");
      await load();
    } catch (e) { toast(e.message || "Couldn't save"); }
    finally { setSavingGroup(false); }
  };

  const sendInvite = async () => {
    setInviting(true);
    try {
      const res = await api.invite(String(id), inviteEmail.trim(), window.location.origin);
      // If real email isn't wired yet, hand the user a ready-to-send link.
      if (res.status === "invited" && res.email_sent === false && res.join_url) {
        try { await navigator.clipboard.writeText(res.join_url); } catch {}
        toast("Email isn't set up — invite link copied. Paste it to them.");
      } else {
        toast(res.message);
      }
      setInviteEmail("");
      setShowInvite(false);
      await load();
    } catch (e) { toast(e.message || "Couldn't invite"); }
    finally { setInviting(false); }
  };

  useEffect(() => {
    if (showInvite && !inviteUrl) {
      api.inviteLink(String(id))
        .then(({ code }) => setInviteUrl(`${window.location.origin}/invite/${code}`))
        .catch(() => {});
    }
  }, [showInvite, inviteUrl, id]);

  const copyLink = async () => {
    try {
      let url = inviteUrl;
      if (!url) {
        const { code } = await api.inviteLink(String(id));
        url = `${window.location.origin}/invite/${code}`;
        setInviteUrl(url);
      }
      await navigator.clipboard.writeText(url);
      toast("Invite link copied — paste it anywhere");
    } catch (e) { toast(e.message || "Couldn't create link"); }
  };

  const shareLink = async () => {
    if (!inviteUrl) return copyLink();
    const payload = { title: `Join ${group?.name || "my group"} on peeps`, text: `Join ${group?.name || "my group"} on peeps 👋`, url: inviteUrl };
    try {
      if (navigator.share) { await navigator.share(payload); return; }
    } catch { return; }
    copyLink();
  };

  const toggleRole = async (memberId, role) => {
    try {
      await api.setRole(String(id), memberId, role === "admin" ? "member" : "admin");
      await load();
    } catch (e) { toast(e.message || "Couldn't update role"); }
  };

  const removeMember = async (memberId, dn) => {
    try {
      await api.removeMember(String(id), memberId);
      setConfirmRemove(null);
      toast(`${dn} removed`);
      await load();
    } catch (e) { toast(e.message || "Couldn't remove"); }
  };

  const toggleMemberInvite = async (val) => {
    setData((p) => ({ ...p, group: { ...p.group, allow_member_invite: val } }));
    try { await api.updateGroup(String(id), { allow_member_invite: val }); } catch { load(); }
  };

  const doLeave = async (newAdminId) => {
    setLeaving(true);
    try {
      const res = await api.leaveGroup(String(id), newAdminId);
      setShowAssign(false);
      toast(res.deleted ? "You left — the empty group was removed" : "You left the group");
      navigate("/", { replace: true });
    } catch (e) {
      if (e.message === "assign_admin_required") {
        setConfirmLeave(false);
        setShowAssign(true);
      } else {
        toast(e.message || "Couldn't leave");
      }
    } finally {
      setLeaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.deleteGroup(String(id));
      toast("Group deleted");
      navigate("/", { replace: true });
    } catch (e) { toast(e.message || "Couldn't delete"); }
  };

  const connectGoogle = async () => {
    if (group.google_synced) {
      await api.googleDisconnect(String(id));
      toast("Disconnected from Google Calendar");
      await load();
      return;
    }
    setConnecting(true);
    try {
      // Sensitive Calendar scope is authorized here (separate from sign-in) via
      // a Google consent redirect that returns to this settings page.
      const { authorization_url } = await api.googleCalendarAuthorize(String(id), window.location.origin);
      window.location.href = authorization_url;
    } catch (e) {
      toast(e.message || "Google Calendar sync unavailable");
      setConnecting(false);
    }
  };

  if (loading || !group) {
    return <div className="flex justify-center items-center min-h-screen" style={{ background: colors.surface }}><Spinner /></div>;
  }

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteEmail.trim());

  return (
    <div className="min-h-screen max-w-2xl mx-auto" style={{ background: colors.surface }}>
      <div className="flex items-center justify-between px-3 py-3">
        <button data-testid="settings-back" onClick={() => navigate(-1)} className="p-2"><ChevronLeft size={20} color={colors.onSurface} /></button>
        <h1 className="font-display font-bold text-[17px]" style={{ color: colors.onSurface }}>Group settings</h1>
        <div className="w-9" />
      </div>

      <div className="p-4 flex flex-col gap-6 pb-16">
        {/* Group hero */}
        <div className="flex flex-col gap-2">
          <div className="relative h-28 rounded-xl overflow-hidden flex items-end p-3" style={group.cover_image_url ? {} : { background: gradientCss(th.grad) }}>
            {group.cover_image_url && <img src={mediaUrl(group.cover_image_url)} alt="" className="absolute inset-0 w-full h-full object-cover" />}
            <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, transparent, rgba(21,22,30,0.8))" }} />
            <span className="relative font-display font-bold text-xl text-white">{group.name}</span>
          </div>
          {isAdmin && (
            <button data-testid="edit-group-button" onClick={openEdit} className="flex items-center justify-center gap-1.5 py-2">
              <Pencil size={16} color={colors.brandSecondary} />
              <span className="font-display font-medium text-[13.5px]" style={{ color: colors.brandSecondary }}>Edit name, color & photo</span>
            </button>
          )}
        </div>

        {/* Invite */}
        <div>
          <SectionLabel>Invite</SectionLabel>
          <Card>
            <ActionRow testID="open-invite-button" onClick={() => setShowInvite(true)} icon={<Mail size={18} color={colors.brandSecondary} />} text="Invite by email" right={<ChevronRight size={16} color={colors.muted} />} />
            <Divider />
            <ActionRow testID="copy-link-button" onClick={copyLink} icon={<LinkIcon size={18} color={colors.brandSecondary} />} text="Copy shareable invite link" right={<Copy size={16} color={colors.muted} />} />
          </Card>
        </div>

        {/* Members */}
        <div>
          <SectionLabel>{members.length} member{members.length === 1 ? "" : "s"}</SectionLabel>
          <Card>
            {members.map((m, i) => (
              <div key={m.user_id}>
                {i > 0 && <Divider />}
                <div className="flex items-center gap-3 p-3">
                  {m.user_id !== user?.id ? (
                    <button data-testid={`member-open-${m.user_id}`} onClick={() => navigate(`/user/${m.user_id}`)} className="flex items-center gap-3 flex-1 min-w-0 text-left">
                      <div className="relative shrink-0">
                        <Avatar name={m.display_name} uri={m.profile_photo_url} size={38} />
                        {statusMap[m.user_id] && (
                          <span className="absolute bottom-0 right-0"><PresenceDot status={statusMap[m.user_id]} size={12} /></span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-display font-medium text-[14.5px] truncate" style={{ color: colors.onSurface }}>{m.display_name}</p>
                        <div className="flex items-center gap-2.5">
                          <span className="font-display text-xs capitalize" style={{ color: colors.faint }}>{m.role}</span>
                          <span className="font-display text-xs" style={{ color: colors.brandSecondary }}>View profile</span>
                        </div>
                      </div>
                    </button>
                  ) : (
                    <>
                      <div className="relative shrink-0">
                        <Avatar name={m.display_name} uri={m.profile_photo_url} size={38} />
                        {statusMap[m.user_id] && (
                          <span className="absolute bottom-0 right-0"><PresenceDot status={statusMap[m.user_id]} size={12} /></span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-display font-medium text-[14.5px] truncate" style={{ color: colors.onSurface }}>{m.display_name} (you)</p>
                        <span className="font-display text-xs capitalize" style={{ color: colors.faint }}>{m.role}</span>
                      </div>
                    </>
                  )}
                  {isAdmin && m.user_id !== user?.id ? (
                    confirmRemove === m.user_id ? (
                      <div className="flex items-center gap-2">
                        <button data-testid={`confirm-remove-${m.user_id}`} onClick={() => removeMember(m.user_id, m.display_name)} className="rounded-md py-1.5 px-2.5 font-display font-bold text-xs" style={{ background: colors.error, color: "#450A0A" }}>Remove</button>
                        <button onClick={() => setConfirmRemove(null)} className="font-display font-medium text-[12.5px]" style={{ color: colors.onSurfaceTertiary }}>Cancel</button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <button data-testid={`role-toggle-${m.user_id}`} onClick={() => toggleRole(m.user_id, m.role)} className="flex items-center gap-1 rounded-full py-1.5 px-2.5" style={{ background: m.role === "admin" ? colors.brandTertiary : colors.surfaceTertiary }}>
                          {m.role === "admin" && <ShieldCheck size={11} color={colors.brandSecondary} />}
                          <span className="font-display font-medium text-xs" style={{ color: m.role === "admin" ? colors.brandSecondary : colors.onSurfaceTertiary }}>{m.role === "admin" ? "Admin" : "Make admin"}</span>
                        </button>
                        <button data-testid={`remove-member-${m.user_id}`} onClick={() => setConfirmRemove(m.user_id)} className="p-1.5 rounded-md" style={{ border: "1px solid #3D2530" }}>
                          <UserMinus size={15} color={colors.error} />
                        </button>
                      </div>
                    )
                  ) : (
                    <span className="flex items-center gap-1 rounded-full py-1.5 px-2.5" style={{ background: m.role === "admin" ? colors.brandTertiary : colors.surfaceTertiary }}>
                      {m.role === "admin" && <ShieldCheck size={11} color={colors.brandSecondary} />}
                      <span className="font-display font-medium text-xs capitalize" style={{ color: m.role === "admin" ? colors.brandSecondary : colors.onSurfaceTertiary }}>{m.role}</span>
                    </span>
                  )}
                </div>
              </div>
            ))}
          </Card>
          {isAdmin && <Hint>Tap a member's role to promote or demote them.</Hint>}
        </div>

        {/* Permissions */}
        {isAdmin && (
          <div>
            <SectionLabel>Permissions</SectionLabel>
            <Card>
              <div className="flex items-center gap-3 p-4">
                <div className="flex-1">
                  <p className="font-display font-medium text-[14.5px]" style={{ color: colors.onSurface }}>Members can invite</p>
                  <p className="font-display text-[12.5px] mt-0.5" style={{ color: colors.faint }}>Let anyone invite & notify the group</p>
                </div>
                <button data-testid="allow-invite-switch" onClick={() => toggleMemberInvite(!group.allow_member_invite)} className="relative inline-flex items-center rounded-full transition-colors shrink-0" style={{ width: 42, height: 24, background: group.allow_member_invite ? colors.brand : colors.surfaceTertiary }}>
                  <span className="absolute rounded-full bg-white transition-transform" style={{ width: 18, height: 18, top: 3, left: 3, transform: group.allow_member_invite ? "translateX(18px)" : "translateX(0)" }} />
                </button>
              </div>
            </Card>
          </div>
        )}

        {/* Google */}
        <div>
          <SectionLabel>Google Calendar</SectionLabel>
          <Card>
            <button data-testid="google-sync-button" onClick={connectGoogle} disabled={connecting} className="flex items-center gap-3 p-4 w-full text-left">
              {group.google_synced ? <CheckCircle2 size={18} color={colors.success} /> : <GoogleIcon size={18} />}
              <span className="flex-1 font-display font-medium text-[14.5px]" style={{ color: colors.onSurface }}>{group.google_synced ? "Synced — tap to disconnect" : "Sync this group to Google Calendar"}</span>
              {connecting ? <Spinner style={{ width: 16, height: 16 }} /> : <ChevronRight size={16} color={colors.muted} />}
            </button>
          </Card>
          <Hint>New plans get pushed to a dedicated Google Calendar for this group.</Hint>
        </div>

        {/* Chat Import Feed (admin-only) */}
        {isAppAdmin && (
          <div data-testid="group-import-feed">
            <SectionLabel>Chat import feed</SectionLabel>
            <Card>
              <div className="p-4">
                <select
                  data-testid="import-feed-select"
                  value={importFeed?.source_group || ""}
                  onChange={(e) => changeFeed(e.target.value)}
                  className="w-full rounded-xl px-3 py-2.5 font-display text-[14px]"
                  style={{ background: colors.surfaceTertiary, border: `1px solid ${colors.border}`, color: colors.onSurface }}
                >
                  <option value="">None — no chat import</option>
                  {(importFeed?.available || []).map((sg) => (
                    <option key={sg} value={sg}>{sg}</option>
                  ))}
                </select>
              </div>
            </Card>
            <Hint>Pick which uploaded chat feed populates this group. Imported plans are silent and appear on the "Added from chat by …" calendar layer. Upload feeds from your Profile.</Hint>
          </div>
        )}

        {/* Calendar sync destination + filter (per-user, per-group) */}
        <div>
          <SectionLabel>Where your plans sync</SectionLabel>
          <Card>
            <button data-testid="cal-dest-dedicated" onClick={() => saveCalPref({ destination: "dedicated_group_calendar", personal_calendar_id: null })} className="flex items-center gap-3 p-4 w-full text-left">
              <CalendarDays size={18} color={colors.brandSecondary} />
              <div className="flex-1">
                <p className="font-display font-medium text-[14.5px]" style={{ color: colors.onSurface }}>Shared group calendar</p>
                <p className="font-display text-[12px]" style={{ color: colors.faint }}>The app's calendar for this group (default)</p>
              </div>
              <Radio checked={calPref?.destination !== "personal_calendar"} />
            </button>
            <Divider />
            <button data-testid="cal-dest-personal" onClick={pickPersonalDestination} className="flex items-center gap-3 p-4 w-full text-left">
              <CalendarDays size={18} color={colors.brandSecondary} />
              <div className="flex-1">
                <p className="font-display font-medium text-[14.5px]" style={{ color: colors.onSurface }}>My own calendar</p>
                <p className="font-display text-[12px]" style={{ color: colors.faint }}>Private sync to one of your Google calendars</p>
              </div>
              {loadingCals ? <span className="spinner" style={{ width: 16, height: 16 }} /> : <Radio checked={calPref?.destination === "personal_calendar"} />}
            </button>
            {calPref?.destination === "personal_calendar" && calendars && (
              <>
                <Divider />
                <div className="p-4">
                  <p className="font-display font-medium text-[11.5px] uppercase tracking-wide mb-2" style={{ color: colors.muted }}>Which calendar</p>
                  <select
                    data-testid="personal-calendar-select"
                    value={calPref.personal_calendar_id || ""}
                    onChange={(e) => saveCalPref({ personal_calendar_id: e.target.value })}
                    className="w-full rounded-xl px-3 py-2.5 font-display text-[14px]"
                    style={{ background: colors.surfaceTertiary, border: `1px solid ${colors.border}`, color: colors.onSurface }}
                  >
                    {calendars.map((c) => (
                      <option key={c.id} value={c.id}>{c.summary}{c.primary ? " (primary)" : ""}</option>
                    ))}
                  </select>
                </div>
              </>
            )}
          </Card>
          <Hint>New plans you're going to sync to your chosen calendar. Only events you RSVP to are added.</Hint>
        </div>

        {/* Personal calendar sharing — visibility tiers (Phase 3) */}
        <div>
          <SectionLabel>Availability sharing</SectionLabel>
          <CalendarSharingSection groupId={String(id)} />
        </div>

        {/* Per-group location sharing */}
        <div>
          <SectionLabel>Location sharing</SectionLabel>
          <Card>
            <div className="flex items-center gap-3 p-4">
              <Navigation size={18} color={groupSharing ? colors.brandSecondary : colors.onSurfaceTertiary} />
              <div className="flex-1">
                <p className="font-display font-medium text-[14.5px]" style={{ color: colors.onSurface }}>Share location at this group's events</p>
                <p className="font-display text-[12px]" style={{ color: colors.faint }}>Overrides your global default for {group.name}</p>
              </div>
              <Toggle testID="group-proximity-toggle" on={groupSharing} onChange={toggleGroupSharing} />
            </div>
          </Card>
          <Hint>Fine-tune the radius and global default in your Profile. Turn it off per-event on the event screen.</Hint>
        </div>

        {/* Danger zone */}
        <div>
          <SectionLabel>Leave / delete</SectionLabel>
          <Card>
            {confirmLeave ? (
              <div className="p-4 flex flex-col gap-3">
                <p className="font-display font-medium text-sm" style={{ color: colors.onSurface }}>Leave {group.name}?</p>
                <div className="flex items-center gap-4">
                  <button data-testid="confirm-leave" onClick={() => doLeave()} disabled={leaving} className="rounded-md py-2 px-4 font-display font-bold text-[13px]" style={{ background: colors.error, color: "#450A0A" }}>{leaving ? "Leaving…" : "Yes, leave"}</button>
                  <button onClick={() => setConfirmLeave(false)} className="font-display font-medium text-[12.5px]" style={{ color: colors.onSurfaceTertiary }}>Cancel</button>
                </div>
              </div>
            ) : (
              <ActionRow testID="leave-group-button" onClick={() => setConfirmLeave(true)} icon={<LogOut size={18} color={colors.warning} />} text="Leave group" textColor={colors.warning} right={<ChevronRight size={16} color={colors.muted} />} />
            )}
            {isAdmin && (
              <>
                <Divider />
                {confirmDelete ? (
                  <div className="p-4 flex flex-col gap-3">
                    <p className="font-display font-medium text-sm" style={{ color: colors.onSurface }}>Delete {group.name} for everyone? This can't be undone.</p>
                    <div className="flex items-center gap-4">
                      <button data-testid="confirm-delete-group" onClick={doDelete} className="rounded-md py-2 px-4 font-display font-bold text-[13px]" style={{ background: colors.error, color: "#450A0A" }}>Delete forever</button>
                      <button onClick={() => setConfirmDelete(false)} className="font-display font-medium text-[12.5px]" style={{ color: colors.onSurfaceTertiary }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <ActionRow testID="delete-group-button" onClick={() => setConfirmDelete(true)} icon={<Trash2 size={18} color={colors.error} />} text="Delete group for everyone" textColor={colors.error} right={<ChevronRight size={16} color={colors.muted} />} />
                )}
              </>
            )}
          </Card>
        </div>
      </div>

      {/* Assign admin */}
      <Sheet visible={showAssign} onClose={() => setShowAssign(false)} title="Pick a new admin" testID="assign-admin-sheet">
        <p className="font-display text-[13.5px] mb-3" style={{ color: colors.onSurfaceTertiary }}>You're the only admin. Choose who takes over before you leave.</p>
        {members.filter((m) => m.user_id !== user?.id).map((m) => (
          <button key={m.user_id} data-testid={`assign-admin-${m.user_id}`} onClick={() => doLeave(m.user_id)} disabled={leaving} className="flex items-center gap-3 w-full py-3" style={{ borderBottom: `1px solid ${colors.border}` }}>
            <Avatar name={m.display_name} uri={m.profile_photo_url} size={36} />
            <span className="flex-1 text-left font-display font-medium text-[14.5px]" style={{ color: colors.onSurface }}>{m.display_name}</span>
            <ShieldCheck size={16} color={colors.brandSecondary} />
          </button>
        ))}
      </Sheet>

      {/* Invite sheet */}
      <Sheet visible={showInvite} onClose={() => setShowInvite(false)} title="Invite to group" testID="invite-sheet">
        <p className="font-display font-medium text-[11.5px] uppercase tracking-wide mb-2 ml-0.5" style={{ color: colors.muted }}>Share invite link</p>
        <div data-testid="invite-link-box" className="rounded-xl p-3 mb-2" style={{ background: colors.surfaceTertiary, border: `1px solid ${colors.border}` }}>
          <p className="font-mono2 text-[12px] break-all" style={{ color: inviteUrl ? colors.onSurface : colors.faint }}>{inviteUrl || "Generating link…"}</p>
        </div>
        <div className="flex gap-2 mb-1">
          <button data-testid="invite-share-button" onClick={shareLink} disabled={!inviteUrl} className="flex-1 flex items-center justify-center gap-1.5 rounded-xl py-3" style={{ background: colors.brand }}>
            <Share2 size={16} color={colors.onBrand} />
            <span className="font-display font-bold text-[13.5px]" style={{ color: colors.onBrand }}>Share link</span>
          </button>
          <button data-testid="invite-copy-button" onClick={copyLink} disabled={!inviteUrl} className="flex items-center justify-center gap-1.5 rounded-xl py-3 px-4" style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}` }}>
            <Copy size={16} color={colors.onSurface} />
            <span className="font-display font-medium text-[13.5px]" style={{ color: colors.onSurface }}>Copy</span>
          </button>
        </div>
        <p className="font-display text-[11.5px] mb-4" style={{ color: colors.faint }}>Anyone with this link can join. Send it via text, WhatsApp, wherever.</p>

        <div className="h-px w-full mb-4" style={{ background: colors.border }} />
        <p className="font-display font-medium text-[11.5px] uppercase tracking-wide mb-2 ml-0.5" style={{ color: colors.muted }}>Or email an invite</p>
        <Field label="Email address">
          <Input testID="invite-email-input" type="email" placeholder="friend@email.com" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} autoCapitalize="none" autoCorrect="off" />
        </Field>
        <PrimaryButton testID="send-invite-button" label="Send email invite" onClick={sendInvite} disabled={!emailValid} loading={inviting} />
      </Sheet>

      {/* Edit group sheet */}
      <Sheet visible={showEdit} onClose={() => setShowEdit(false)} title="Edit group" testID="edit-group-sheet">
        <Field label="Cover photo">
          <button data-testid="edit-cover-picker" onClick={chooseCover} disabled={uploadingCover} className="relative w-full h-28 rounded-xl overflow-hidden flex items-center justify-center" style={cover ? {} : { background: gradientCss(groupThemes[theme].grad) }}>
            {cover ? <img src={mediaUrl(cover)} alt="" className="absolute inset-0 w-full h-full object-cover" /> : <span className="font-display font-bold text-2xl" style={{ color: "rgba(255,255,255,0.9)" }}>{initials(name || "G")}</span>}
            <span className="absolute flex items-center justify-center rounded-full" style={{ width: 28, height: 28, bottom: 8, right: 8, background: colors.brand }}><Camera size={13} color={colors.onBrand} /></span>
          </button>
          {cover && <button data-testid="remove-cover" onClick={() => setCover(null)} className="w-full mt-2 font-display font-medium text-[12.5px]" style={{ color: colors.onSurfaceTertiary }}>Remove photo, use color instead</button>}
        </Field>
        <Field label="Group name">
          <Input testID="edit-group-name" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Color">
          <div className="flex gap-3 flex-wrap">
            {Object.keys(groupThemes).map((key) => (
              <button key={key} data-testid={`edit-theme-${key}`} onClick={() => setTheme(key)} className="p-[3px] rounded-full" style={{ border: `2px solid ${theme === key ? colors.onSurface : "transparent"}` }}>
                <span className="block rounded-full" style={{ width: 36, height: 36, background: gradientCss(groupThemes[key].grad) }} />
              </button>
            ))}
          </div>
        </Field>
        <PrimaryButton testID="save-group-button" label="Save changes" onClick={saveGroup} disabled={!name.trim()} loading={savingGroup} />
      </Sheet>
    </div>
  );
}

// helpers
function SectionLabel({ children }) {
  return <p className="font-display font-medium text-[11.5px] uppercase tracking-wide mb-2 ml-1" style={{ color: colors.muted }}>{children}</p>;
}
function Card({ children }) {
  return <div className="rounded-xl overflow-hidden" style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}` }}>{children}</div>;
}
function Divider() {
  return <div style={{ height: 1, background: colors.border, marginLeft: 16 }} />;
}
function Radio({ checked }) {
  return (
    <span className="w-[20px] h-[20px] rounded-full flex items-center justify-center shrink-0" style={{ border: `2px solid ${checked ? colors.brand : colors.borderStrong}` }}>
      {checked && <span className="w-2.5 h-2.5 rounded-full" style={{ background: colors.brand }} />}
    </span>
  );
}
function Hint({ children }) {
  return <p className="font-display text-xs mt-2 ml-1" style={{ color: colors.faint }}>{children}</p>;
}
function ActionRow({ testID, onClick, icon, text, textColor = colors.onSurface, right }) {
  return (
    <button data-testid={testID} onClick={onClick} className="flex items-center gap-3 p-4 w-full text-left">
      {icon}
      <span className="flex-1 font-display font-medium text-[14.5px]" style={{ color: textColor }}>{text}</span>
      {right}
    </button>
  );
}

// Pencil import shim
function Pencil(props) {
  return <svg width={props.size} height={props.size} viewBox="0 0 24 24" fill="none" stroke={props.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>;
}
